import { useEffect, useMemo, useState } from "react";
import { subscribeToEvents } from "../services/eventsService";

const toDate = (value) => {
  if (!value) {
    return null;
  }
  if (value.toDate) {
    return value.toDate();
  }
  return new Date(value);
};

const formatDate = (date) => {
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat("hi-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const EventCard = ({ event }) => (
  <article className="event-card">
    <header>
      <h3>{event.titleHindi}</h3>
      <span className="caption">{event.titleEnglish}</span>
      <p className="event-date">{formatDate(event.eventDate)}</p>
    </header>
    <p>{event.descriptionHindi}</p>
    <span className="caption">{event.descriptionEnglish}</span>
    {event.images && event.images.length > 0 && (
      <div className="event-images">
        {event.images.map((image) => (
          <figure key={image.id}>
            <img
              src={image.url}
              alt={image.captionEnglish || event.titleEnglish}
              loading="lazy"
            />
            {(image.captionHindi || image.captionEnglish) && (
              <figcaption>
                <span>{image.captionHindi}</span>
                <span className="caption">{image.captionEnglish}</span>
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    )}
  </article>
);

const EventsPage = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribeToEvents((items) => {
      setEvents(
        items.map((event) => ({
          ...event,
          eventDate: toDate(event.eventDate),
        })),
      );
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const { upcoming, archived } = useMemo(() => {
    const today = new Date();
    const upcomingEvents = [];
    const archivedEvents = [];
    events.forEach((event) => {
      if (!event.eventDate || event.eventDate >= today) {
        upcomingEvents.push(event);
      } else {
        archivedEvents.push(event);
      }
    });

    const sortAsc = (a, b) => {
      if (!a.eventDate && !b.eventDate) {
        return 0;
      }
      if (!a.eventDate) {
        return 1;
      }
      if (!b.eventDate) {
        return -1;
      }
      return a.eventDate - b.eventDate;
    };

    const sortDesc = (a, b) => {
      if (!a.eventDate && !b.eventDate) {
        return 0;
      }
      if (!a.eventDate) {
        return 1;
      }
      if (!b.eventDate) {
        return -1;
      }
      return b.eventDate - a.eventDate;
    };

    return {
      upcoming: upcomingEvents.sort(sortAsc),
      archived: archivedEvents.sort(sortDesc),
    };
  }, [events]);

  return (
    <section className="events-page">
      <header>
        <h2>आयोजन और संगोष्ठियाँ</h2>
        <span className="caption">Programs and Gatherings</span>
        <p>
          आर्य समाज अबोहर समाज कल्याण, आध्यात्मिक जागरण और सांस्कृतिक पुनरुत्थान
          हेतु नियमित आयोजन करता है।
        </p>
        <span className="caption">
          Regular programs in Abohar nurture community welfare, spiritual
          awakening, and cultural renaissance.
        </span>
      </header>

      <section className="events-section">
        <h3>आगामी आयोजन</h3>
        <span className="caption">Upcoming Events</span>
        {upcoming.length > 0 ? (
          <div className="events-grid">
            {upcoming.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <p>कोई आगामी आयोजन सूचीबद्ध नहीं है।</p>
        )}
      </section>

      <section className="events-section">
        <h3>पुरालेख</h3>
        <span className="caption">Archives</span>
        {archived.length > 0 ? (
          <div className="events-grid">
            {archived.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <p>पुराने आयोजनों का संग्रह तैयार किया जा रहा है।</p>
        )}
      </section>
    </section>
  );
};

export default EventsPage;
